import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import { findIndex } from 'lodash';
import moment from 'moment';
import i18n from 'i18n';
import scaleSize from 'utils/scale-size';
import VerticalList from 'components/VerticalList';
import { ReactComponent as InfoIconSVG } from 'assets/img/information-icon.svg';
import { StyledEpgItemWrapper, StyledChannelItem, StyledEPGProgramItemData } from './StyledComponents';
import config from 'config';
import getLocalized from 'utils/get-localized';

const LIST_CONFIG = {
  defaultElement: '.current-program',
  enterTo: 'last-focused',
  leaveFor: {
    down: '',
    up: '',
    right: '',
  },
};

export const StyledListWrapper = styled.div`
  display: inline-block;
`;

export const StyledEPGList = styled(VerticalList)`
  width: ${scaleSize(920)}px;
  height: ${scaleSize(540)}px;
  margin-top: -${scaleSize(167)}px;
  margin-left: -${scaleSize(70)}px;
  display: inline-block;
  vertical-align: top;
  padding-left: ${scaleSize(70)}px;
`;

export const StyledEPGHeader = styled.div`
  width: ${scaleSize(635)}px;
  height: ${scaleSize(180)}px;
  box-sizing: border-box;
  position: relative;

  & > span {
    position: absolute;
    bottom: ${scaleSize(34)}px;
    left: ${scaleSize(20)}px;
    font-size: ${scaleSize(40)}px;
  }

  &:after {
    width: ${scaleSize(840)}px;
    height: ${scaleSize(2)}px;
    background: ${({ theme }) => theme.defaultColor};
    opacity: 0.3;
    content: '';
    position: absolute;
    left: ${scaleSize(20)}px;
    bottom: ${scaleSize(13)}px;
  }
`;

const StyledInfoIcon = styled(InfoIconSVG)`
  display: block;
  float: right;
  top: -${scaleSize(44)}px;
  position: relative;
`;

const EPGItem = ({ id, item, onEnter, onFocus, isDVR, isVod, index, width = 880, activeProgramIndex }) => {
  const { title, subTitle, state, startTime, endTime, description, descriptions } = item;
  const now = moment().add(config.timeDiffFromServer, 'm');
  const startTimeKievTime = moment(startTime);
  const endTimeKievTime = moment(endTime);
  const hasPassed = endTimeKievTime < now;
  const isLive = startTimeKievTime < now && endTimeKievTime > now;
  const isActive = activeProgramIndex === index;
  const isCatchUpAvailable = isVod || (hasPassed && isDVR && state === 'CATCHUP');
  const hasDescription = useMemo(() => {
    const d = getLocalized(descriptions, descriptions.UA || description);
    return !!d && d !== '--';
  }, [description, descriptions]);

  let label = isLive && i18n.t('epg.onAir');
  if (isCatchUpAvailable) label = i18n.t('epg.catchup');

  return (
    <StyledEpgItemWrapper
      id={id}
      onEnter={() => onEnter(index)}
      onFocus={onFocus}
      hasPassed={hasPassed}
      className={isActive ? 'current-program' : ''}
      index={index}
      width={width}
      type={'program'}
    >
      <StyledChannelItem>
        <StyledEPGProgramItemData isCatchUpAvailable={isCatchUpAvailable} isActive={isActive} isLive={isLive}>
          <span>{title}</span>
          {hasDescription && <StyledInfoIcon />}
          <i>{subTitle}</i>
          <b>{label}</b>
        </StyledEPGProgramItemData>
      </StyledChannelItem>
    </StyledEpgItemWrapper>
  );
};

EPGItem.Header = (props) => (
  <StyledEPGHeader>
    <span>{props.item.title}</span>
  </StyledEPGHeader>
);

/*
 * Подмешивает props для элемента листа и выполняет условный рендер
 * Позволяет оставить сам лист чистым и абстрактным компонентом
 * https://reactjs.org/docs/render-props.html
 */
const EPGProgramRenderFunc = (props, { isDVR, isVodChannel, activeProgramIndex }) => {
  const { isHeader } = props.item;

  return isHeader ? (
    <EPGItem.Header {...props} />
  ) : (
    <EPGItem {...props} isDVR={isDVR} isVod={isVodChannel} activeProgramIndex={activeProgramIndex} />
  );
};

export const ProgramsList = ({
  sectionName,
  focusedChannel,
  currentPlayingProgramId,
  currentPlayingChannelId,
  onFocus,
  onEnter,
  onListFocus,
}) => {
  const items = focusedChannel.epg;
  const isDVR = !!focusedChannel.tvServices.catchupTv;
  const isVodChannel = !!focusedChannel.isVod;
  const liveProgramId = focusedChannel.currentProgram?.id;

  const activeProgramIndex = useMemo(() => {
    // Only for currently playing channel
    if (focusedChannel.id !== currentPlayingChannelId) return -1;
    // Catchup case
    if (currentPlayingProgramId) {
      return findIndex(items, { id: currentPlayingProgramId });
    }
    // live case
    return findIndex(items, { id: liveProgramId });
  }, [currentPlayingChannelId, currentPlayingProgramId, focusedChannel, items, liveProgramId]);

  const defaultIndex = useMemo(() => {
    if (activeProgramIndex !== -1) return activeProgramIndex;

    return findIndex(items, { id: liveProgramId });
  }, [activeProgramIndex, items, liveProgramId]);

  return (
    <StyledListWrapper>
      <StyledEPGList
        listId={'epg-list-wrapper'}
        sectionName={sectionName}
        items={items}
        itemRender={(props) => EPGProgramRenderFunc(props, { isDVR, isVodChannel, activeProgramIndex })}
        defaultIndex={defaultIndex}
        itemHeight={180}
        onFocus={onFocus}
        onEnter={onEnter}
        onListFocus={onListFocus}
        config={LIST_CONFIG}
        renderSize={8}
      />
    </StyledListWrapper>
  );
};

export default ProgramsList;
